import { Anchor, PinInput, Stack, Text, TextInput } from '@mantine/core';
import { t } from 'i18next';
import { useState } from 'react';

const AUTHENTICATOR_CODE_LENGTH = 6;

type TwoFactorCodeInputProps = {
	value: string;
	onChange: (value: string) => void;
	/** Lets the user switch to a recovery code. Off where only the authenticator app is asked for. */
	allowRecoveryCode?: boolean;
	error?: string;
	disabled?: boolean;
	label?: string;
};

/** A code from the authenticator app, with an option to type a recovery code instead. */
export function TwoFactorCodeInput({
	value,
	onChange,
	allowRecoveryCode = true,
	error,
	disabled,
	label,
}: TwoFactorCodeInputProps) {
	const [useRecoveryCode, setUseRecoveryCode] = useState(false);

	const toggle = () => {
		setUseRecoveryCode(current => !current);
		onChange('');
	};

	return (
		<Stack gap="xs">
			{useRecoveryCode ? (
				<TextInput
					label={t('twoFactor.code.recoveryLabel')}
					placeholder="xxxxx-xxxxx"
					value={value}
					onChange={event => onChange(event.currentTarget.value)}
					error={error}
					disabled={disabled}
					autoCapitalize="none"
					autoComplete="off"
					autoCorrect="off"
					spellCheck={false}
					data-autofocus
				/>
			) : (
				<Stack gap={4}>
					<Text size="sm" fw={500}>
						{label ?? t('twoFactor.code.label')}
					</Text>
					<PinInput
						type="number"
						length={AUTHENTICATOR_CODE_LENGTH}
						oneTimeCode
						value={value}
						onChange={onChange}
						error={!!error}
						disabled={disabled}
						data-autofocus
					/>
					{error && (
						<Text size="xs" c="red">
							{error}
						</Text>
					)}
				</Stack>
			)}
			{allowRecoveryCode && (
				<Anchor size="sm" component="button" type="button" onClick={toggle} disabled={disabled} ta="start">
					{useRecoveryCode ? t('twoFactor.code.useApp') : t('twoFactor.code.useRecovery')}
				</Anchor>
			)}
		</Stack>
	);
}
